import { useEffect } from 'react';

/**
 * Whether an element is one that summons the on-screen keyboard — text-like
 * inputs, textareas, and contenteditable surfaces (the chat composer).
 * Button-like inputs never raise a keyboard. Mirrors the signal used by
 * {@link import('./useOnScreenKeyboardOpen')}.
 */
function summonsOnScreenKeyboard(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color'].includes(
      el.type,
    );
  }
  return false;
}

/**
 * Pins an `--app-height` CSS variable on `<html>` to the visual-viewport height
 * while the on-screen keyboard is open, so full-height surfaces (the app shell,
 * the mobile launch sheet — anything using the `h-app` / `max-h-app` utilities)
 * shrink to the area *above* the keyboard instead of extending behind it.
 *
 * Why this is needed: iOS Safari overlays the software keyboard on top of the
 * layout viewport without shrinking it, so `100dvh` / `100vh` stay full-height
 * and the bottom of the page (footers, submit buttons, the composer) is
 * stranded behind the keyboard with no way to scroll to it. Android with
 * `interactive-widget=resizes-content` already shrinks the layout viewport, so
 * this is a no-op there. The `visualViewport` API is the only cross-engine
 * signal for the visible height.
 *
 * Scoped to the keyboard-open window (coarse pointer + a focused keyboard-
 * summoning element) so ordinary address-bar show/hide viewport changes never
 * resize the app and cause scroll jank. The variable is cleared when the
 * keyboard closes, falling back to the `100dvh` default baked into `h-app`.
 */
export function useKeyboardViewportInset(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    const root = document.documentElement;
    let raf = 0;

    const apply = () => {
      cancelAnimationFrame(raf);
      // focusout fires before the next element gains focus, so read
      // `document.activeElement` a frame later — moving focus between two
      // fields must not flash the closed state (and full-height jump) between.
      raf = requestAnimationFrame(() => {
        if (summonsOnScreenKeyboard(document.activeElement)) {
          root.style.setProperty('--app-height', `${Math.round(vv.height)}px`);
          // iOS Safari may have already scrolled the document to bring the
          // focused input into view. Undo that scroll so the app shell
          // (pinned at the viewport top by `useDocumentScrollLock`) stays in
          // place and the `h-app` height reduction alone handles fitting
          // content above the keyboard.
          if (window.scrollY !== 0) window.scrollTo(0, 0);
        } else {
          root.style.removeProperty('--app-height');
        }
      });
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    document.addEventListener('focusin', apply);
    document.addEventListener('focusout', apply);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      document.removeEventListener('focusin', apply);
      document.removeEventListener('focusout', apply);
      root.style.removeProperty('--app-height');
    };
  }, []);
}
